import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { applyShellUpdate, registerShellBuild, shellWorkerRegistration } from './worker-registration';
import { allowOfflineInstall, offlineInstallStore } from './offline-install';

export function PWAUpdate() {
  const { t } = useTranslation('shell');
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const paused = useSyncExternalStore(offlineInstallStore.subscribe, offlineInstallStore.getSnapshot);
  const reloadOnController = useRef(false);
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    let active = true;
    let registration: ServiceWorkerRegistration | undefined;
    let checking = false;
    const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src') ?? '/';
    const reportBuild = () => { if (active) navigator.serviceWorker.controller?.postMessage({ type: 'HYLO_SHELL_CLIENT', build: script }); };
    const onMessage = (event: MessageEvent) => { if (event.data?.type === 'HYLO_SHELL_IDENTIFY') reportBuild(); };
    const watch = (next: ServiceWorkerRegistration) => {
      registration = next;
      if (next.waiting && navigator.serviceWorker.controller && active) setWaiting(next.waiting);
      next.addEventListener('updatefound', () => {
        const worker = next.installing;
        worker?.addEventListener('statechange', () => {
          if (active && worker.state === 'installed' && navigator.serviceWorker.controller) setWaiting(worker);
        });
      });
    };
    void shellWorkerRegistration()?.then(watch).then(() => navigator.serviceWorker.ready).then(reportBuild).catch(() => { /* PWA support never blocks the app. */ });
    const check = async () => {
      if (document.visibilityState !== 'visible' || checking || !registration) return;
      checking = true;
      try {
        const response = await fetch('/', { cache: 'no-store', credentials: 'omit', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(10_000) });
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src');
        if (active && next && next !== script && /^\/assets\/[^/?]+\.js$/.test(next)) {
          const nextRegistration = await registerShellBuild(next);
          if (active) watch(nextRegistration);
        }
      } catch { /* Offline launch continues from the installed shell. */ } finally { checking = false; }
    };
    const onController = () => {
      if (reloadOnController.current) window.location.reload();
      else {
        reportBuild();
        const controller = navigator.serviceWorker.controller;
        if (controller && new URL(controller.scriptURL).searchParams.get('build') !== script) setWaiting(controller);
      }
    };
    const onVisibility = () => { reportBuild(); void check(); };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    navigator.serviceWorker.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      navigator.serviceWorker.removeEventListener('controllerchange', onController);
      navigator.serviceWorker.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  if (!waiting) return paused ? <aside className="shell-update-prompt" role="status"><p>{t('offlineSetupPaused')}</p><p>{t('offlineSetupDescription')}</p>
    <button className="shell-primary" onClick={allowOfflineInstall}>{t('prepareOffline')}</button></aside> : null;
  return <aside className="shell-update-prompt" role="status"><p>{t('updateReady')}</p><div>
    <button className="shell-secondary" onClick={() => setWaiting(null)}>{t('later')}</button>
    <button className="shell-primary" onClick={() => {
      reloadOnController.current = true;
      applyShellUpdate(waiting, navigator.serviceWorker.controller, () => window.location.reload());
    }}>{t('update')}</button>
  </div></aside>;
}
